import { useQuery } from '@tanstack/react-query'
import { useUser } from 'features/authorized/context/user'
import { UserData } from 'features/common/core/entitites'
import { useTypedTranslation } from 'i18n'
import getStatistics from './api/getStatistics'
import getStars from './api/getStars'
import getTotalTips from './api/getTotalTips'

const monthNames: { [x: string]: string } = {
    January: 'JAN',
    February: 'FEB',
    March: 'MAR',
    April: 'APR',
    May: 'MAY',
    June: 'JUN',
    July: 'JUL',
    August: 'AUG',
    September: 'SEP',
    October: 'OCT',
    November: 'NOV',
    December: 'DEC',
}

const useStatistics = () => {
    const { user } = useUser() as { user: UserData }
    const { t: typedT } = useTypedTranslation()
    const {
        country,
        currency,
        organization: { organizationId, stripeAccountId },
    } = user
    const { data, isFetching } = useQuery(['statistics', 'tips'], () =>
        getStatistics({ country, stripeAccountId }),
    )
    const { data: stars, isFetching: isStarsFetching } =
        useQuery(['statistics', 'stars'], () => getStars(organizationId))

    const { data: totalTips, isFetching: isTotalTipsFetching } =
        useQuery(['statistics', 'total', 'tips'], () => getTotalTips({ organizationId, currency }))

    if (data) {
        const translatedCharts = data.balance.map((cd) => ({
            name: typedT(monthNames[cd.month]),
            amount: cd.amount / 100,
        }))
        const sortedCharts = [...translatedCharts].reverse()
        const formattedTotalTips = totalTips ? (totalTips / 100) : 0
        return {
            stars: stars || 0,
            totalTips: formattedTotalTips,
            data: sortedCharts,
            currency: data.currency,
            isLoading: isFetching || isStarsFetching || isTotalTipsFetching,
        }
    }

    return { data: null, stars: 0, totalTips: 0, currency: null, isLoading: isFetching }
}

export default useStatistics
